import { IconChevronUp, IconX } from "@tabler/icons-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import type { AssistantDataParts } from "../../../../api/src/shared/assistant-ui-message";
import { Button } from "../../components/ui/Button";
import { cn } from "../../lib/utils";
import { WIZARD_DARK_SPRING, WIZARD_EXIT } from "./wizardMotion";
import { useWizardNoteCollapsed } from "./wizardSession";
import { WizardSpotlight } from "./WizardSpotlight";

/**
 * The note on screen (docs/juiscigpt-wizard.md). Note 1 has three screens that
 * follow the answer (REQ-06) and no "Suivant". Note 2 points at the sources
 * once the answer is complete (REQ-07). Note 3 sends the reader to the trial
 * (REQ-08).
 */
export type WizardNoteView =
  | { note: 1; screen: 1 }
  | {
      note: 1;
      screen: 2;
      readCount: number;
      searchCounts: AssistantDataParts["searchCounts"] | null;
    }
  | { note: 1; screen: 3 }
  | { note: 2 }
  | { note: 3 };

// The targets that the pages mark with `data-wizard-target`.
const TARGETS = {
  1: '[data-wizard-target="progress"]',
  2: '[data-wizard-target="sources"]',
  3: null,
} as const satisfies Record<WizardNoteView["note"], string | null>;

/**
 * A note of the wizard in the place of the composer, over the blurred page.
 * Its close control and a tap outside it fold it into a bar; a tap on the bar
 * opens it again (REQ-15). "Quitter la visite" ends the tour (REQ-10).
 */
export function WizardNote({
  view,
  layout,
  onNext,
  onAsk,
  onQuit,
}: {
  view: WizardNoteView;
  // On desktop the note sits at the bottom of the content area, beside the
  // sidebar, and note 2 points at the Sources panel (Figma D-A2 to D-A4).
  layout: "mobile" | "desktop";
  onNext: () => void;
  onAsk: () => void;
  onQuit: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion() === true;
  const [collapsed, setCollapsed] = useWizardNoteCollapsed();
  const copy = useNoteCopy(view, layout);
  const width = layout === "desktop" ? "max-w-lg" : "max-w-md";
  const fold = (): void => {
    setCollapsed(true);
  };
  const bottom = {
    paddingBottom: "calc(var(--safe-area-bottom) + 1rem)",
  } as const;

  if (collapsed)
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4"
        style={bottom}
      >
        <button
          type="button"
          className={cn(
            "flex w-full items-center justify-between gap-3 rounded-2xl bg-ds-mint-900/95 px-4 py-3 text-left text-sm text-ds-cream shadow-lg",
            width
          )}
          onClick={() => {
            setCollapsed(false);
          }}
        >
          <span className="truncate">
            {t("Note {{number}} · {{title}}", {
              number: view.note,
              title: copy.title,
            })}
          </span>
          <IconChevronUp className="size-5 shrink-0" stroke={1.5} />
        </button>
      </div>
    );

  return (
    <>
      <WizardSpotlight
        targetSelector={TARGETS[view.note]}
        onTapOutside={fold}
      />
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4"
        style={bottom}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.section
            key={view.note}
            aria-live="polite"
            className={cn(
              "relative flex w-full flex-col gap-3 rounded-3xl bg-ds-mint-900/95 p-5 text-ds-cream shadow-xl",
              width
            )}
            initial={reducedMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={
              reducedMotion
                ? { opacity: 0, transition: { duration: 0 } }
                : { opacity: 0, transition: WIZARD_EXIT }
            }
            transition={reducedMotion ? { duration: 0 } : WIZARD_DARK_SPRING}
          >
            <button
              type="button"
              aria-label={t("Fold the note")}
              className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-full text-ds-mint-100 hover:bg-ds-cream/10"
              onClick={fold}
            >
              <IconX className="size-5" stroke={1.5} />
            </button>
            <ol className="flex gap-1.5" aria-label={t("Tour progress")}>
              {([1, 2, 3] as const).map((note) => (
                <li
                  key={note}
                  className={cn(
                    "h-1 w-6 rounded-full",
                    note <= view.note ? "bg-ds-mint-400" : "bg-ds-cream/20"
                  )}
                />
              ))}
            </ol>
            <div className="flex flex-col gap-1.5 pr-8">
              <h2 className="text-lg font-semibold leading-tight text-balance">
                {copy.title}
              </h2>
              {copy.body.map((paragraph) => (
                <p key={paragraph} className="text-sm text-ds-mint-100">
                  {paragraph}
                </p>
              ))}
            </div>
            {view.note === 1 ? null : (
              <Button
                type="button"
                variant="accent"
                size="md"
                className="w-full"
                onClick={view.note === 2 ? onNext : onAsk}
              >
                {view.note === 2 ? t("Next") : t("Ask a question")}
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="self-center text-ds-mint-100"
              onClick={onQuit}
            >
              {t("Leave the tour")}
            </Button>
          </motion.section>
        </AnimatePresence>
      </div>
    </>
  );
}

// Every sentence states what the engine does (REQ-31): it ranks by relevance,
// prefers guidelines, and cites next to what a source supports.
function useNoteCopy(
  view: WizardNoteView,
  layout: "mobile" | "desktop"
): {
  title: string;
  body: string[];
} {
  const { t } = useTranslation();
  switch (view.note) {
    case 1:
      switch (view.screen) {
        case 1:
          return {
            title: t("Here is how it finds the answer"),
            body: [
              t(
                "Each step shows above. It searches society guidelines and publications."
              ),
              t("The answer takes about 45 seconds."),
            ],
          };
        // REQ-33: the found, kept and read counts, once the engine streams them.
        case 2:
          return {
            title: t("It keeps the most relevant publications"),
            body:
              view.searchCounts === null
                ? [
                    t(
                      "It prefers guidelines and reads the publications it kept."
                    ),
                    t("{{count}} articles read", { count: view.readCount }),
                  ]
                : [
                    t(
                      "Out of {{count}} publications found, it keeps {{kept}}.",
                      {
                        count: view.searchCounts.found,
                        kept: view.searchCounts.kept,
                      }
                    ),
                    t("{{count}} articles read", { count: view.readCount }),
                    t("Guidelines come first."),
                  ],
          };
        case 3:
          return {
            title: t("The answer is coming"),
            body: [
              t(
                "It writes from the sources it found, and cites each one next to what it supports."
              ),
            ],
          };
        default:
          throw new Error(view satisfies never);
      }
    case 2:
      return {
        title: t("Check the sources"),
        body: [
          layout === "desktop"
            ? t(
                "The Sources panel, on the right, gathers every reference. Each number leads to its source."
              )
            : t(
                "The Sources tab gathers every reference. Each number leads to its source."
              ),
        ],
      };
    case 3:
      return {
        title: t("Put JuisciGPT to the test"),
        body: [
          t(
            "Ask a clinical question you know the answer to: a threshold, a delay, a course of action. Check whether it reaches your conclusion, and on which sources."
          ),
        ],
      };
    default:
      throw new Error(view satisfies never);
  }
}
